/**
 * Shared PKR conversion for the Historical tab.
 *
 * The tab's "Energy / PKR" toggle has to cost energy that was metered against
 * different tariffs: peak vs off-peak grid hours, and grid vs genset vs solar
 * sources. `client_profiles` stores one rate per case (GET /get_customer_info),
 * so every chart resolves its rate here instead of each one re-deriving a single
 * `unitPrice` and quietly costing genset units at the grid rate.
 */

const CURRENCY_CODE = "PKR";

/** Parse a numeric price from API or localStorage; null if missing/invalid. */
export function parsePriceNumber(value) {
  if (value == null || value === "") return null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** First parseable price among the candidates, else null. */
function firstPrice(...candidates) {
  for (const candidate of candidates) {
    const parsed = parsePriceNumber(candidate);
    if (parsed != null) return parsed;
  }
  return null;
}

/**
 * Same, but a non-positive rate counts as "not configured".
 *
 * Used for the optional tariffs only. Some profiles carry a literal 0 in a
 * tariff column (prod client `jawa` has peak_unit_price = 0.00) — that is a
 * placeholder, not free electricity, and taking it at face value would cost the
 * whole peak segment at 0 and empty its share of the Productivity bar.
 */
function firstPositivePrice(...candidates) {
  for (const candidate of candidates) {
    const parsed = parsePriceNumber(candidate);
    if (parsed != null && parsed > 0) return parsed;
  }
  return null;
}

/** Prices cached by /signin; the backend sends them snake_case, older writes used camelCase. */
function readStoredUserPricing() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}") || {};
  } catch {
    return {};
  }
}

/**
 * Per-kWh rates for every Historical chart, resolved once per render.
 *
 * `pricing` is the /get_customer_info payload (HistoricalTab keeps it in
 * `clientProfilePricing`); the cached /signin user is the fallback for clients
 * whose profile call failed. Every optional rate falls back to the grid rate
 * rather than to 0, so an unset genset/solar/peak tariff shows an approximate
 * cost instead of collapsing a bar to nothing. Only ~4 of 54 prod clients have
 * any tariff beyond `unit_price` set, so that fallback is the common path.
 */
export function resolveTariffs(pricing) {
  const stored = readStoredUserPricing();

  // The grid rate is the base everything else falls back to, so it is taken at
  // face value — there is nothing further to fall back to.
  const grid = firstPrice(pricing?.unit_price, stored?.unit_price) ?? 0;
  const peak =
    firstPositivePrice(pricing?.peak_unit_price, stored?.peak_unit_price) ?? grid;
  const genset =
    firstPositivePrice(
      pricing?.gen_unitprice,
      pricing?.gen_unitPrice,
      stored?.gen_unitprice,
      stored?.gen_unitPrice,
    ) ?? grid;
  const solar =
    firstPositivePrice(
      pricing?.solar_unitprice,
      pricing?.solar_unitPrice,
      stored?.solar_unitprice,
      stored?.solar_unitPrice,
    ) ?? grid;

  return {
    grid,
    peak,
    offPeak: grid,
    genset,
    solar,
    // No export/buyback column exists on client_profiles, so exported units are
    // credited at the solar rate when the client has one (closest stand-in for a
    // net-metering rate) and at the grid rate otherwise.
    exportCredit: solar,
  };
}

/** Rate for a machine's `category` as returned by the hierarchy endpoints. */
export function rateForCategory(category, tariffs) {
  const normalized = (category || "").toUpperCase();
  if (normalized === "GENERATOR") return tariffs?.genset ?? 0;
  if (normalized === "SOLAR") return tariffs?.solar ?? 0;
  return tariffs?.grid ?? 0;
}

/** `kWh` or `PKR` for axis titles, table headers and card units. */
export function unitLabel(isPkr, energyUnit = "kWh") {
  return isPkr ? CURRENCY_CODE : energyUnit;
}

/**
 * "1,234.56" — grouped so five- and six-figure PKR values stay readable next to
 * the two-decimal kWh values the same labels show in Energy mode.
 */
export function formatAmount(value, decimals = 2) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0.00";
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}
